import { Currency } from "@redotech/money/currencies";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { CreateFulfillmentOrder } from "@redotech/redo-model/fulfillments/create-fulfillment-order";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { memo, useEffect, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { LocationContext } from "../services/location-service";
import {
  manualOrderForm,
  ManualOrderFormValue,
  ManualOrderSlideout,
} from "./manual-order-form";

export const CreateOrderSlideout = memo(function CreateOrderSlideout({
  onClose,
  open,
  addGroupsToTable,
  defaultValues,
  redoOrderId,
  orderId,
}: {
  onClose: () => void;
  open: boolean;
  addGroupsToTable: (groups: FulfillmentGroupData[]) => void;
  defaultValues?: Partial<ManualOrderFormValue>;
  redoOrderId?: string | null;
  orderId?: string | null;
}) {
  const [locationsLoad, _] = useLazyContext(LocationContext);

  const defaultOrder: ManualOrderFormValue = {
    location: defaultValues?.location ?? null,
    address: {
      name: defaultValues?.address?.name ?? "",
      address1: defaultValues?.address?.address1 ?? "",
      address2: defaultValues?.address?.address2 ?? "",
      city: defaultValues?.address?.city ?? "",
      company: defaultValues?.address?.company ?? "",
      countryCode:
        defaultValues?.address?.countryCode ?? RedoShopifyCountryCode.Us,
      email: defaultValues?.address?.email ?? "",
      phone: defaultValues?.address?.phone ?? "",
      province: defaultValues?.address?.province ?? "Utah",
      zip: defaultValues?.address?.zip ?? "",
    },
    orderInfo: {
      orderNumber: defaultValues?.orderInfo?.orderNumber ?? "",
      orderDate: defaultValues?.orderInfo?.orderDate ?? new Date(),
      paidDate: defaultValues?.orderInfo?.paidDate ?? new Date(),
      shippingPaid: defaultValues?.orderInfo?.shippingPaid ?? {
        amount: "0",
        currency: Currency.USD,
      },
      taxPaid: defaultValues?.orderInfo?.taxPaid ?? {
        amount: "0",
        currency: Currency.USD,
      },
      totalPaid: defaultValues?.orderInfo?.totalPaid ?? {
        amount: "0",
        currency: Currency.USD,
      },
      items: defaultValues?.orderInfo?.items ?? [],
      internalNote: defaultValues?.orderInfo?.internalNote ?? "",
    },
  };

  const form = useInput(manualOrderForm, defaultOrder);
  const [itemTableResetKey, setItemTableResetKey] = useState(0);

  useEffect(() => {
    const firstLocation = locationsLoad.value?.at(0);
    if (firstLocation && !form.value.location) {
      form.inputs.location.setValue(firstLocation);
    }
  }, [locationsLoad.value, form]);

  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const rpcCall = useHandler(
    async (
      createFulfillmentOrder: CreateFulfillmentOrder,
    ): Promise<{
      success: boolean;
      fulfillmentGroups: FulfillmentGroupData[];
    }> => {
      const result = await rpcClient.createManualFulfillmentGroups({
        fulfillmentOrders: [
          {
            ...createFulfillmentOrder,
            redoOrderId: redoOrderId ?? undefined,
            orderId: orderId ?? undefined,
          },
        ],
      });
      return {
        success: result.success,
        fulfillmentGroups: result.fulfillmentGroups ?? [],
      };
    },
  );

  const handleSuccess = useHandler((groups: FulfillmentGroupData[]) => {
    addGroupsToTable(groups);
    form.setValue({
      location: null,
      address: {
        name: "",
        address1: "",
        address2: "",
        city: "",
        company: "",
        countryCode: RedoShopifyCountryCode.Us,
        email: "",
        phone: "",
        province: "Utah",
        zip: "",
      },
      orderInfo: {
        orderNumber: "",
        orderDate: new Date(),
        paidDate: new Date(),
        shippingPaid: { amount: "0", currency: Currency.USD },
        taxPaid: { amount: "0", currency: Currency.USD },
        totalPaid: { amount: "0", currency: Currency.USD },
        items: [],
        internalNote: "",
      },
    });
    setItemTableResetKey((k) => k + 1);
  });

  return (
    <ManualOrderSlideout
      errorMessage="Failed to create order"
      form={form}
      itemTableResetKey={itemTableResetKey}
      locations={locationsLoad.value ?? []}
      onClose={onClose}
      onSuccess={handleSuccess}
      open={open}
      rpcCall={rpcCall}
      submitButtonText="Create order"
      successMessage="Order created"
      title="Create new order"
    />
  );
});
